import { useApolloClient } from '@apollo/client'

import { useUserContext } from '@/contexts/UserContext.tsx'
import {
  addCacheListElement,
  createCacheEntity,
  getEntity,
  redefineTaskOrder,
  removeCacheEntity,
  removeCacheListElement,
} from '@/requests/apollo/cache.ts'
import { getMutation } from '@/requests/apollo/mutation.ts'
import {
  BLOCKER_COMMAND_META,
  BLOCKER_GQL_SCHEMA,
  COMMENT_COMMAND_META,
  COMMENT_GQL_SCHEMA,
  TASK_COMMAND_META,
  TASK_GQL_SCHEMA,
} from '@/requests/apollo/schema.ts'
import { getExecuteTemplate } from '@/requests/apollo/templates.ts'
import { useUpdateCache } from '@/requests/apollo/useUpdateCache.ts'
import { useQueryProjectMeta } from '@/requests/queries/useQueryProject.ts'
import { isTemporaryId } from '@/utils/key.ts'

const useMutateEntity = <T extends BaseChildEntity>(
  entityType: ChildEntity,
  commandMeta: EntityCommandMeta,
  entityScheme: string,
  entityId?: string,
) => {
  // biome-ignore lint/style/noNonNullAssertion: This is guaranteed to be non-null
  const user = useUserContext().user!
  const { projectMeta } = useQueryProjectMeta()
  const { updateStory } = useUpdateCache()
  const { cache } = useApolloClient()

  const [execute] = getMutation(entityType, commandMeta)

  return {
    create: (entity: T) => {
      const requestedEntity = { ...entity, __typename: entityType }

      if (isTemporaryId(entity.storyId)) {
        createCacheEntity(requestedEntity, entityType, entityScheme)
        return Promise.resolve()
      }

      const { variables, optimisticResponse } = getExecuteTemplate(
        projectMeta,
        `${entityType.toUpperCase()}_CREATE`,
        user.id,
        entity,
        requestedEntity,
      )
      return execute({
        variables,
        optimisticResponse,
        update: (cache, { data: { executeCommand } }) => {
          for (const createdEntity of executeCommand.data) {
            if (entityType === 'Blocker' && createdEntity.__typename === 'Story') {
              updateStory(createdEntity.id, { blockedStoryIds: createdEntity })
              // } else if (entityType === 'Label') {
            } else {
              addCacheListElement<BaseChildEntity>(
                cache.identify({ __typename: 'Story', id: entity.storyId }),
                createdEntity,
                `${entityType.toLowerCase()}s`,
              )
            }
          }

          if (entityType === 'Task' && executeCommand.data.length > 0) {
            redefineTaskOrder(cache.identify({ __typename: 'Story', id: executeCommand.data[0].storyId }))
          }
        },
        onError: (error) => {
          notifyError(error)
          if (getEntity(entityType, entityScheme, entityId)) return

          removeCacheListElement(
            cache.identify({ __typename: 'Story', id: entity.storyId }),
            `${entityType.toLowerCase()}s`,
            entity.id,
          )

          cache.evict({ id: cache.identify(requestedEntity) })
        },
      })
    },
    update: (params: Partial<T>) => {
      const entity = getEntity<T>(entityType, entityScheme, entityId)
      if (!entity) return Promise.reject(new Error(`Entity ${entityType} with id ${entityId} not found`))
      if (isTemporaryId(entity.storyId)) return Promise.resolve()

      const { variables, optimisticResponse } = getExecuteTemplate(
        projectMeta,
        `${entityType.toUpperCase()}_UPDATE`,
        user.id,
        {
          ...entity,
          ...params,
        },
        {
          ...entity,
          ...params,
        },
      )
      return execute({
        variables,
        optimisticResponse,
        update: (_, { data: { executeCommand } }) => {
          for (const updatedEntity of executeCommand.data) {
            if (entityType === 'Blocker' && updatedEntity.__typename === 'Story') {
              updateStory(updatedEntity.id, { blockedStoryIds: updatedEntity })
            }
          }
        },
        onError: (error) => {
          notifyError(error)
        },
      })
    },
    remove: (entityId: string) => {
      const requestedEntity = getEntity<T>(entityType, entityScheme, entityId)
      if (!requestedEntity) return Promise.reject(new Error(`Entity ${entityType} with id ${entityId} not found`))

      if (isTemporaryId(requestedEntity.storyId)) {
        removeCacheEntity(requestedEntity, entityType)
        return Promise.resolve()
      }

      const { variables, optimisticResponse } = getExecuteTemplate(
        projectMeta,
        `${entityType.toUpperCase()}_DELETE`,
        user.id,
        {
          id: entityId,
          storyId: requestedEntity.storyId,
        },
        requestedEntity,
      )

      return execute({
        variables,
        optimisticResponse,
        update: (cache, { data: { executeCommand } }) => {
          for (const deletedEntity of executeCommand.data) {
            if (entityType === 'Blocker' && deletedEntity.__typename === 'Story') {
              updateStory(deletedEntity.id, { blockedStoryIds: deletedEntity })
            } else {
              removeCacheListElement(
                cache.identify({ __typename: 'Story', id: deletedEntity.storyId }),
                `${entityType.toLowerCase()}s`,
                deletedEntity.id,
              )
            }
          }

          if (entityType === 'Task' && executeCommand.data.length === 1) {
            redefineTaskOrder(cache.identify({ __typename: 'Story', id: executeCommand.data[0].storyId }))
          }
        },
        onCompleted: () => {
          cache.evict({ id: cache.identify(requestedEntity) })
        },
        onError: (error) => {
          notifyError(error)
          if (getEntity(entityType, entityScheme, entityId)) return

          addCacheListElement(
            cache.identify({ __typename: 'Story', id: requestedEntity.storyId }),
            requestedEntity,
            `${entityType.toLowerCase()}s`,
          )
        },
      })
    },
  }
}

const notifyError = (error: Error) => console.error(error)

export const useBlockerMutation = (blockerId?: string) =>
  useMutateEntity<Blocker>('Blocker', BLOCKER_COMMAND_META, BLOCKER_GQL_SCHEMA, blockerId)

export const useCommentMutation = (commentId?: string) =>
  useMutateEntity<StoryComment>('Comment', COMMENT_COMMAND_META, COMMENT_GQL_SCHEMA, commentId)

export const useTaskMutation = (commentId?: string) =>
  useMutateEntity<Task>('Task', TASK_COMMAND_META, TASK_GQL_SCHEMA, commentId)
